import React, { useEffect, useState } from "react";
import { useGetLiveSessionsQuery } from "../../api/api";
import { formatDateTime, useDecodedTokenResult } from "../../lib/utils";
import { IoCalendarOutline } from "react-icons/io5";
import { MdLaptopMac, MdOutlineLiveTv } from "react-icons/md";
import { Button } from "../../@/components/ui/button";
import { IoMdTime } from "react-icons/io";
import SessionSkeleton from "../../components/skeletons/SessionSkeleton";
import { IData, ISession, SessionListProps } from "../../lib/interfaces";
import { useBroadcastManagerContext } from "./components/BroadcastManagerContext";

const SessionList: React.FC<SessionListProps> = ({
  onSessionSelect,
  refetchSessions,
  onCreateSession,
}) => {
  const vendorId = useDecodedTokenResult().vendorId!;
  const [sessions, setSessions] = useState<IData[]>([]);
  const isMobile = window.innerWidth < 768;
  const { startBroadcast, streamStatus, broadcaster } =
    useBroadcastManagerContext();

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useGetLiveSessionsQuery(
    { vendorId },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (response?.data) {
      setSessions(response.data as unknown as IData[]);
    }
  }, [response]);

  useEffect(() => {
    const refetchFunction = async () => {
      return await refetch();
    };

    if (refetchSessions) {
      refetchSessions(refetchFunction);
    }
  }, [refetch, refetchSessions]);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="min-h-[75vh] max-w-3xl mx-auto">
          <SessionSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Error loading sessions</div>;
  }

  // Separate active and ended sessions
  const { activeSessions, endedSessions } = sessions.reduce(
    (acc, session) => {
      if (session.status === "Ended") {
        acc.endedSessions.push(session);
      } else {
        acc.activeSessions.push(session);
      }
      return acc;
    },
    { activeSessions: [] as IData[], endedSessions: [] as IData[] }
  );

  // Function to group sessions by date
  const groupSessionsByDate = (sessions: IData[]) => {
    return sessions.reduce((acc, session) => {
      const dateObj = new Date(session.startDateTime);
      if (isNaN(dateObj.getTime())) return acc;

      const date = dateObj.toISOString().split("T")[0];
      if (!acc[date]) acc[date] = [];
      acc[date].push(session);

      // Sort sessions within same date by startDateTime
      acc[date].sort((a, b) => {
        return (
          new Date(a.startDateTime).getTime() -
          new Date(b.startDateTime).getTime()
        );
      });

      return acc;
    }, {} as Record<string, IData[]>);
  };

  const groupedActiveSessions = groupSessionsByDate(activeSessions);
  const groupedEndedSessions = groupSessionsByDate(endedSessions);

  // Sort dates for both sections
  const sortDates = (dates: string[], ascending = false) => {
    return dates.sort((a, b) => {
      const comparison = new Date(a).getTime() - new Date(b).getTime();
      return ascending ? -comparison : comparison;
    });
  };

  const activeDates = sortDates(Object.keys(groupedActiveSessions), true); // Sort ascending for active sessions
  const endedDates = sortDates(Object.keys(groupedEndedSessions)); // Sort descending for ended sessions

  const convertToSession = (data: IData): ISession => {
    return {
      id: data.id,
      title: data.title,
      startDateTime: data.startDateTime,
      products: data.products.map((product) => ({
        productId: product.productId,
        defaultImageUrl: product.defaultImageUrl,
        productName: product.productName,
        quantityAvailable: 0,
      })),
    };
  };

  const isDateExpired = (date: string | Date): boolean => {
    const inputDate = new Date(date);
    if (isNaN(inputDate.getTime())) return false; // Prevents errors
    return inputDate.getTime() < new Date().getTime();
  };

  return (
    <div className="min-h-[75vh] p-4 mb-8">
      <div
        className={`min-h-[75vh] mx-auto w-full max-w-xl ${
          isMobile ? "mb-20 pb-6" : ""
        }`}
      >
        {/* Mobile Create Session Button */}
        <div className="md:hidden mb-4 mt-2">
          <button
            onClick={onCreateSession}
            className="w-full bg-customGreenColor text-white p-4 rounded-lg shadow-md hover:bg-customGreenColor/90 transition-all flex items-center justify-center gap-2 font-medium"
          >
            <MdOutlineLiveTv className="w-6 h-6" />
            <span>Create New Session</span>
          </button>
        </div>

        {sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-500 mt-16 bg-gray-50 rounded-xl p-8">
            <MdOutlineLiveTv className="w-20 h-20 mb-4 text-gray-400" />
            <p className="text-xl font-semibold mb-2">No Sessions Found</p>
            <p className="text-gray-600 text-center">
              Get started by creating your first live session
            </p>
          </div>
        ) : (
          <>
            {/* Live Sessions Section */}
            {activeDates.length > 0 && (
              <div className="mb-12">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                  <MdOutlineLiveTv className="w-7 h-7 text-customGreenColor" />
                  <span>Live Sessions</span>
                </h2>
                {activeDates.map((date) => (
                  <div key={date} className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-600 mb-4 flex items-center gap-2 sticky top-0 bg-gray-50/80 backdrop-blur-sm py-3 z-10">
                      <IoCalendarOutline size={20} className="text-gray-600" />
                      {formatDateTime(date, "ddd, MMM DD, YYYY")}
                    </h3>
                    <div className="space-y-4">
                      {groupedActiveSessions[date].map((session) => (
                        <div
                          key={session.id}
                          className="bg-white rounded-lg shadow-sm border border-gray-200 w-full transition-all hover:shadow-md hover:border-gray-300 overflow-hidden"
                        >
                          <div className="p-5">
                            <div className="flex items-start gap-3 mb-3">
                              <MdLaptopMac
                                size={22}
                                className="text-gray-600 mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between gap-3">
                                  <h4 className="text-lg font-semibold text-gray-900 leading-tight line-clamp-2 break-all">
                                    {session.title}
                                  </h4>
                                  <span
                                    className={`text-xs font-medium px-3 py-1 rounded-full ml-2 ${
                                      session.status === "Created"
                                        ? "text-blue-800 bg-blue-100"
                                        : session.status === "Ended"
                                        ? "text-red-800 bg-red-100"
                                        : "text-green-800 bg-green-100"
                                    }`}
                                  >
                                    {session.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="ml-9">
                              <div className="text-gray-600 text-sm flex items-center gap-2">
                                <IoMdTime size={16} className="text-gray-500" />
                                <span className="text-gray-600">
                                  {formatDateTime(
                                    session.startDateTime,
                                    "h:mm A"
                                  )}
                                  {session.endDateTime && (
                                    <span>
                                      {" "}
                                      —{" "}
                                      {formatDateTime(
                                        session.endDateTime,
                                        "h:mm A"
                                      )}
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                          {session.status !== "Ended" && (
                            <div className="flex items-center gap-3 p-4 bg-gray-50 border-t border-gray-100 flex-wrap">
                              {session.status === "Started" ? (
                                !streamStatus && !broadcaster ? (
                                  <Button
                                    className="bg-customGreenColor hover:bg-customGreenColor/90 font-medium flex items-center gap-2"
                                    onClick={() => startBroadcast(session)}
                                  >
                                    <MdOutlineLiveTv className="w-5 h-5" />
                                    Rejoin
                                  </Button>
                                ) : null
                              ) : (
                                !isDateExpired(session.startDateTime) && (
                                  <Button
                                    className="bg-customGreenColor hover:bg-customGreenColor/90 font-medium flex items-center gap-2"
                                    onClick={() => startBroadcast(session)}
                                  >
                                    <MdOutlineLiveTv className="w-5 h-5" />
                                    Go Live
                                  </Button>
                                )
                              )}
                              {session.status !== "Started" && (
                                <Button
                                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium"
                                  onClick={() =>
                                    onSessionSelect(convertToSession(session))
                                  }
                                >
                                  Reschedule
                                </Button>
                              )}
                              {session.status === "Started" && streamStatus && (
                                <div className="text-amber-600 text-sm">
                                  This session is currently being streamed
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Ended Sessions Section */}
            {endedDates.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-3">
                  <IoMdTime className="w-7 h-7 text-gray-600" />
                  <span>Past Sessions</span>
                </h2>
                {endedDates.map((date) => (
                  <div key={date} className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-600 mb-4 flex items-center gap-2 sticky top-0 bg-gray-50/80 backdrop-blur-sm py-3 z-10">
                      <IoCalendarOutline size={20} className="text-gray-600" />
                      {formatDateTime(date, "ddd, MMM DD, YYYY")}
                    </h3>
                    <div className="space-y-4">
                      {groupedEndedSessions[date].map((session) => (
                        <div
                          key={session.id}
                          className="bg-gray-50 rounded-lg border border-gray-200 w-full transition-all hover:bg-gray-100"
                        >
                          <div className="p-5">
                            <div className="flex items-start gap-3">
                              <MdLaptopMac
                                size={22}
                                className="text-gray-500 mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-lg font-semibold text-gray-900 leading-tight line-clamp-2 break-all">
                                    {session.title}
                                  </h4>
                                  <span
                                    className={`text-xs font-medium px-3 py-1 rounded-full ml-2 ${
                                      session.status === "Created"
                                        ? "text-blue-800 bg-blue-100"
                                        : session.status === "Ended"
                                        ? "text-red-800 bg-red-100"
                                        : "text-green-800 bg-green-100"
                                    }`}
                                  >
                                    {session.status}
                                  </span>
                                </div>
                                <div className="text-gray-600 text-sm flex items-center gap-2 mt-2">
                                  <IoMdTime size={16} />
                                  <span className="text-gray-600">
                                    {formatDateTime(
                                      session.startDateTime,
                                      "h:mm A"
                                    )}
                                    {session.endDateTime && (
                                      <span>
                                        {" "}
                                        —{" "}
                                        {formatDateTime(
                                          session.endDateTime,
                                          "h:mm A"
                                        )}
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SessionList;
